/**
 * QuizConsent (Sprint 6 B6) — pre-exam consent screen.
 *
 * Flow:
 *   1. User click "Bắt đầu thi" trên QuizList → /quizzes/:id/consent
 *   2. Component fetch Quiz info (qua /api/assessment/quizzes/?course=... or
 *      qua start attempt early then read back? — em chọn start attempt ngay,
 *      grant consent xong navigate vào /take. Start idempotent nên OK)
 *   3. Show consent text (Nghị định 13/PIPA/APPI compliant)
 *   4. DeviceCheck pre-flight (fullscreen + webcam nếu required)
 *   5. Tick "Tôi đồng ý" required → button "Bắt đầu thi" enable
 *   6. Click → POST grant consent → navigate /quizzes/attempts/<id>/take
 *
 * UX: full-page card, gradient background subtle, không AppLayout sidebar
 * (focus mode trước thi).
 */

import { useEffect, useState } from "react";
import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  Checkbox,
  Result,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from "antd";
import {
  ClockCircleOutlined,
  HomeOutlined,
  RightOutlined,
  SafetyCertificateOutlined,
  TrophyOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useStartAttempt } from "@/api/assessment";
import { useGrantConsent } from "@/api/proctoring";
import { DeviceCheck } from "@/components/DeviceCheck";

const { Title, Paragraph, Text } = Typography;

export function QuizConsent() {
  const { quizId } = useParams<{ quizId: string }>();
  const navigate = useNavigate();
  const qid = quizId ? Number(quizId) : undefined;

  const [accepted, setAccepted] = useState(false);
  const [deviceOK, setDeviceOK] = useState(false);
  const [starting, setStarting] = useState(false);

  const startMut = useStartAttempt();

  // Start attempt ngay khi mount (idempotent — đã có IN_PROGRESS thì return cũ).
  // Cần attempt để biết proctor_session_id + webcam_required từ quiz.
  useEffect(() => {
    if (!qid || startMut.isPending || startMut.data) return;
    startMut.mutate(qid, {
      onError: (err: unknown) => {
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 409) {
          message.warning("Bạn đã hết lượt làm bài cho quiz này.");
        } else if (status === 403) {
          message.error("Bạn cần đăng ký môn học trước.");
        }
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qid]);

  const attempt = startMut.data;
  const grantMut = useGrantConsent(attempt?.proctor_session_id ?? undefined);

  if (startMut.isPending || !attempt) {
    return (
      <div style={{ textAlign: "center", padding: 64 }}>
        <Spin size="large" />
        <div style={{ marginTop: 12 }}>Chuẩn bị bài thi...</div>
      </div>
    );
  }

  if (startMut.error || !attempt.proctor_session_id) {
    return (
      <Result
        status="warning"
        title="Không bắt đầu được bài thi"
        subTitle="Vui lòng kiểm tra lại đăng ký môn học."
        extra={<Link to="/my-courses">← Về khóa của tôi</Link>}
      />
    );
  }

  // Nếu attempt đã grant consent từ trước (reload mid-quiz) → skip thẳng vào take
  if (attempt.consent_given_at) {
    navigate(`/quizzes/attempts/${attempt.id}/take`, { replace: true });
    return null;
  }

  const handleStart = async () => {
    setStarting(true);
    try {
      await grantMut.mutateAsync();
      navigate(`/quizzes/attempts/${attempt.id}/take`);
    } catch {
      message.error("Không gửi được consent. Vui lòng thử lại.");
      setStarting(false);
    }
  };

  const canStart = accepted && deviceOK && !starting;

  return (
    <div style={{ maxWidth: 800, margin: "0 auto" }} data-testid="quiz-consent">
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          { title: <Link to="/"><HomeOutlined /></Link> },
          { title: <Link to="/my-courses">Khóa của tôi</Link> },
          { title: "Trước khi thi" },
        ]}
      />

      <Card>
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <div>
            <Title level={2} style={{ margin: 0 }}>
              <TrophyOutlined /> {attempt.quiz.title}
            </Title>
            <Space wrap style={{ marginTop: 8 }}>
              <Tag color="blue" icon={<ClockCircleOutlined />}>
                {attempt.quiz.duration_display}
              </Tag>
              <Tag color="green">
                Đạt: ≥{attempt.quiz.pass_threshold_pct}%
              </Tag>
              {attempt.quiz.webcam_required && (
                <Tag color="orange" icon={<VideoCameraOutlined />}>
                  Bắt buộc webcam
                </Tag>
              )}
            </Space>
          </div>

          <Alert
            type="info"
            showIcon
            icon={<SafetyCertificateOutlined />}
            message="Điều khoản giám sát thi cử"
            description={
              <div style={{ fontSize: 14 }}>
                <Paragraph>
                  Tôi đồng ý hệ thống ghi hình <strong>webcam</strong> + chụp{" "}
                  <strong>screenshot</strong> trong suốt bài thi nhằm mục đích{" "}
                  <strong>chống gian lận thi cử</strong>. Dữ liệu được lưu trong{" "}
                  <strong>90 ngày</strong> và sẽ tự động xoá sau thời gian này.
                  Tôi có quyền yêu cầu xem hoặc xoá dữ liệu của mình bất kỳ lúc
                  nào bằng cách liên hệ <strong>dpo@savina.vn</strong>.
                </Paragraph>
                <Paragraph style={{ marginBottom: 0 }}>
                  Hệ thống sẽ phát hiện và ghi lại các hành vi: chuyển tab, thoát
                  fullscreen, sao chép/dán văn bản. Mọi vi phạm có thể dẫn đến{" "}
                  <strong>huỷ kết quả thi</strong>.
                </Paragraph>
              </div>
            }
          />

          <DeviceCheck
            webcamRequired={attempt.quiz.webcam_required}
            onResult={setDeviceOK}
          />

          <Checkbox
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            data-testid="consent-checkbox"
          >
            <Text strong>Tôi đã đọc và đồng ý với các điều khoản trên.</Text>
          </Checkbox>

          <Button
            type="primary"
            size="large"
            block
            icon={<RightOutlined />}
            disabled={!canStart}
            loading={starting}
            onClick={handleStart}
            data-testid="start-exam-btn"
          >
            Bắt đầu thi ({attempt.quiz.duration_display})
          </Button>
        </Space>
      </Card>
    </div>
  );
}

export default QuizConsent;
